/**
 * Carousel Cell Accessibility
 * Keeps keyboard focus on the Flickity cells that are visible: off-screen cells
 * are hidden from assistive tech and Tab moves between the visible cells.
 * The cells stay hit-testable (no inert) so a card can be clicked wherever it is drawn.
 * @param {object} opts
 */
function carouselCellAccessibility(opts) {
  const config = {
    cellClass: "js-products-carousel-cell",
    cellVisibleRatio: 0.5
  };
  const c = __.extend(config, opts);

  function getCellFocusables($cell) {
    return [...$cell.querySelectorAll("a[href], button:not([disabled])")];
  }

  function getCellVisibleRatio($cell, $viewport) {
    const cellRect = $cell.getBoundingClientRect();
    const viewportRect = $viewport.getBoundingClientRect();
    const visibleWidth =
      Math.min(cellRect.right, viewportRect.right) - Math.max(cellRect.left, viewportRect.left);

    return cellRect.width > 0 ? visibleWidth / cellRect.width : 0;
  }

  function getVisualCells(flickity) {
    const $viewport = flickity?.viewport;
    if (!$viewport || !flickity.cells?.length) {
      return [];
    }

    return flickity.cells
      .map((cell) => cell.element)
      .filter(($cell) => getCellVisibleRatio($cell, $viewport) >= c.cellVisibleRatio)
      .sort((a, b) => a.getBoundingClientRect().left - b.getBoundingClientRect().left);
  }

  function getFocusedCell(flickity) {
    if (!flickity.element.contains(document.activeElement)) {
      return null;
    }

    return document.activeElement.closest(`.${c.cellClass}`);
  }

  function setCellTabEntry(flickity, $entry) {
    flickity.cells.forEach((cell) => {
      getCellFocusables(cell.element).forEach(($el) => {
        $el.tabIndex = $el === $entry ? 0 : -1;
      });
    });
  }

  function syncCellAccessibility(flickity) {
    const $viewport = flickity?.viewport;
    if (!$viewport || !flickity.cells?.length) {
      return;
    }

    const $visualCells = getVisualCells(flickity);
    const $focusedCell = getFocusedCell(flickity);

    flickity.cells.forEach((cell) => {
      const $cell = cell.element;

      if ($visualCells.includes($cell) || $cell === $focusedCell) {
        $cell.removeAttribute("aria-hidden");
      } else {
        $cell.setAttribute("aria-hidden", "true");
      }
    });

    const $focused = $focusedCell ? document.activeElement : null;
    const [$entryFocusable] = $visualCells.length ? getCellFocusables($visualCells[0]) : [];

    setCellTabEntry(flickity, $focused || $entryFocusable);
  }

  function handleCellTab(e, flickity) {
    if (e.key !== "Tab") {
      return;
    }

    // lazy loaded images resize the cells, so the state is refreshed before acting on it
    syncCellAccessibility(flickity);

    const $visualCells = getVisualCells(flickity);
    if (!$visualCells.length) {
      return;
    }

    const $currentCell = e.target.closest(`.${c.cellClass}`);
    const currentIndex = $visualCells.indexOf($currentCell);

    if (currentIndex === -1) {
      const [$prevButton] = flickity.element.querySelectorAll(".flickity-prev-next-button");

      // shift-tabbing back into the carousel lands on the rightmost visible cell
      if (e.shiftKey && e.target === $prevButton) {
        const lastFocusables = getCellFocusables($visualCells[$visualCells.length - 1]);
        const $target = lastFocusables[lastFocusables.length - 1];

        if ($target) {
          e.preventDefault();
          $target.focus({ preventScroll: true });
        }
      }

      return;
    }

    const focusables = getCellFocusables($currentCell);
    const focusIndex = focusables.indexOf(e.target.closest("a[href], button"));
    const $nextInCell = focusables[e.shiftKey ? focusIndex - 1 : focusIndex + 1];

    if (focusIndex !== -1 && $nextInCell) {
      e.preventDefault();
      $nextInCell.focus({ preventScroll: true });
      return;
    }

    const $nextCell = $visualCells[e.shiftKey ? currentIndex - 1 : currentIndex + 1];
    if (!$nextCell) {
      return;
    }

    const nextFocusables = getCellFocusables($nextCell);
    const $target = e.shiftKey ? nextFocusables[nextFocusables.length - 1] : nextFocusables[0];
    if (!$target) {
      return;
    }

    e.preventDefault();
    $target.focus({ preventScroll: true });
  }

  function handleCellFocus(e, flickity) {
    if (!e.target.closest(`.${c.cellClass}`)) {
      return;
    }

    setCellTabEntry(flickity, e.target);
  }

  // Flickity moves the slider with transforms, so a native scroll of the viewport (focus,
  // find in page) would draw the cells away from where the synced state expects them
  function handleViewportScroll(flickity) {
    flickity.viewport.scrollLeft = 0;
  }

  function bindCellAccessibility(flickity) {
    if (!flickity) {
      return;
    }

    const sync = () => syncCellAccessibility(flickity);

    sync();
    flickity.on("ready", sync);
    flickity.on("select", sync);
    flickity.on("settle", sync);
    flickity.on("lazyLoad", sync);

    const onKeydown = (e) => handleCellTab(e, flickity);
    const onFocusin = (e) => handleCellFocus(e, flickity);

    if (flickity.element._cellTabHandler) {
      flickity.element.removeEventListener("keydown", flickity.element._cellTabHandler);
    }
    if (flickity.element._cellFocusHandler) {
      flickity.element.removeEventListener("focusin", flickity.element._cellFocusHandler);
    }

    flickity.element._cellTabHandler = onKeydown;
    flickity.element._cellFocusHandler = onFocusin;
    flickity.element.addEventListener("keydown", onKeydown);
    flickity.element.addEventListener("focusin", onFocusin);
    flickity.viewport.addEventListener("scroll", () => handleViewportScroll(flickity));
  }

  function clearCellAccessibility(cells) {
    [...cells].forEach(($cell) => {
      $cell.removeAttribute("aria-hidden");
      getCellFocusables($cell).forEach(($el) => $el.removeAttribute("tabindex"));
    });
  }

  return {
    bind: bindCellAccessibility,
    clear: clearCellAccessibility,
    sync: syncCellAccessibility
  };
}

export default carouselCellAccessibility;
